import { cn } from '../../lib/cn';
import { signedPoints } from '../../lib/format';
import type { CardVM } from '../../lib/round';
import { MarketDots, MultiplierBadge } from './shared';
import { ChevronRight, Lock, Pencil } from '../ui/icons';

/** Matchup title — codes with the live scoreline picked out in accent while in play. */
function MatchupTitle({ pick }: { pick: CardVM }) {
  if (pick.status === 'live' && pick.liveScore) {
    return (
      <span className="num text-[14px] font-extrabold tracking-snug text-white">
        {pick.homeCode} <span className="text-accent">{pick.liveScore}</span> {pick.awayCode}
      </span>
    );
  }
  return <span className="num text-[14px] font-extrabold tracking-snug text-white">{pick.fixtureLabel}</span>;
}

/** A live clock pill ("67'") with a pulsing dot — the per-pick liveness signal. */
function LiveClock({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-[5px] rounded-md bg-accent/[0.16] px-[7px] py-[2px]">
      <span className="h-[5px] w-[5px] animate-h2h-pulse rounded-full bg-accent" />
      <span className="num text-[10px] font-extrabold tracking-wide text-accent">{label}</span>
    </span>
  );
}

/**
 * One pick on the live board. While in play it shows the running scoreline, the
 * minute, each market lit up on-track/trailing, and the points it would bank if
 * the match ended now. After full-time it reads the settled hits and final points;
 * before kickoff it offers an Edit shortcut while the fixture is still open.
 *
 * The whole row is tappable — it opens the match screen (your prediction, the live
 * timeline and stats) — while the inline Edit shortcut keeps its own tap target.
 */
export function PickOverviewRow({
  pick,
  onOpen,
  onEdit
}: {
  pick: CardVM;
  onOpen?: () => void;
  onEdit?: () => void;
}) {
  const isLive = pick.status === 'live';
  const isUpcoming = pick.status === 'upcoming';
  const tappable = !!onOpen;

  return (
    <div
      role={tappable ? 'button' : undefined}
      tabIndex={tappable ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        tappable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpen?.();
              }
            }
          : undefined
      }
      className={cn(
        'rounded-[20px] p-[14px_15px]',
        tappable && 'tap cursor-pointer text-left active:bg-white/[0.07]',
        isLive
          ? 'border-[1.5px] border-accent bg-accent/[0.07] shadow-[0_12px_30px_-16px_rgba(36,197,106,0.5)]'
          : 'border border-white/[0.08] bg-white/[0.05]'
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex w-[34px] flex-col items-center gap-[3px]">
          <MultiplierBadge multiplier={pick.multiplier} active={isLive} dark />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <MatchupTitle pick={pick} />
            {isLive && pick.liveClock && <LiveClock label={pick.liveClock} />}
            {pick.status === 'final' && <span className="text-[10px] font-bold tracking-wide text-white/40">FT</span>}
            {pick.status === 'void' && <span className="text-[10px] font-bold tracking-wide text-white/40">VOID</span>}
            {isUpcoming && pick.kickoffLabel && (
              <span className="num text-[10px] font-bold tracking-wide text-white/55">{pick.kickoffLabel}</span>
            )}
          </div>
          <div className="mt-2 flex items-center gap-[6px]">
            <MarketDots markets={pick.markets} dark />
            <span
              className={cn(
                'text-[10px] font-semibold',
                isLive ? 'text-accent' : 'text-white/55'
              )}
            >
              {pick.marketSummary}
            </span>
          </div>
        </div>

        {isUpcoming && pick.editable ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.();
            }}
            className="tap flex items-center gap-[5px] rounded-[9px] bg-white/10 px-[11px] py-2 active:bg-white/[0.16]"
          >
            <Pencil width={12} height={12} strokeWidth={2.2} className="text-white" />
            <span className="text-[12px] font-bold text-white">Edit</span>
          </button>
        ) : isUpcoming ? (
          <Lock width={14} height={14} strokeWidth={2.4} className="text-white/30" />
        ) : isLive && pick.livePoints != null ? (
          <div className="text-right">
            <div className="num text-[18px] font-extrabold leading-none text-accent">
              {signedPoints(pick.livePoints)}
            </div>
            <div className="mt-[3px] text-[10px] font-semibold text-white/45">projected</div>
          </div>
        ) : pick.points != null ? (
          <div className="text-right">
            <div
              className={cn(
                'num text-[18px] font-extrabold leading-none',
                pick.points > 0 ? 'text-accent' : 'text-white'
              )}
            >
              {signedPoints(pick.points)}
            </div>
            <div className="mt-[3px] text-[10px] font-semibold text-white/45">final</div>
          </div>
        ) : null}

        {tappable && (
          <ChevronRight width={16} height={16} strokeWidth={2.6} className="-mr-1 shrink-0 text-white/30" />
        )}
      </div>
    </div>
  );
}
